import { useCallback, useEffect, useMemo, useState } from "react";
import { deleteRow, insertRow, listRows } from "@/lib/adminApi";
import { MESSAGE_STATUS, metaFor, type OutboundMessage } from "@/lib/adminContent";
import {
  DataTable,
  DetailList,
  Drawer,
  EmptyState,
  ErrorBanner,
  Field,
  FilterBar,
  LoadingState,
  Modal,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  TextField,
  btnGhost,
  btnPrimary,
  fieldInput,
  textInput,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { formatDate } from "@/pages/admin/format";
import { useAdminToast } from "@/hooks/useAdminToast";

export interface MessageChannelConfig {
  channel: string;
  eyebrow: string;
  title: string;
  subtitle: string;
  icon: string;
  composeTitle: string;
  recipientLabel: string;
  recipientPlaceholder: string;
  subjectLabel: string;
  subjectPlaceholder: string;
}

export default function MessageCenter({ config }: { config: MessageChannelConfig }) {
  const [rows, setRows] = useState<OutboundMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [draft, setDraft] = useState({ recipient: "", subject: "", body: "" });
  const [saving, setSaving] = useState(false);
  const { notify, node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const all = await listRows<OutboundMessage>("outbound_messages", "sent_at");
      setRows(all.filter((row) => row.channel === config.channel));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load messages.");
    } finally {
      setLoading(false);
    }
  }, [config.channel]);

  useEffect(() => {
    load();
  }, [load]);

  const send = async () => {
    if (!draft.recipient.trim() || !draft.body.trim()) {
      setError("A recipient and a message body are required.");
      return;
    }
    setSaving(true);
    try {
      const created = await insertRow<OutboundMessage>("outbound_messages", {
        channel: config.channel,
        recipient: draft.recipient.trim(),
        subject: draft.subject.trim() || null,
        body: draft.body.trim(),
        status: "sent",
        sent_at: new Date().toISOString(),
      });
      setRows((list) => [created, ...list]);
      setComposeOpen(false);
      setDraft({ recipient: "", subject: "", body: "" });
      notify("Message sent.");
    } catch {
      setError("We couldn't send that message. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: number) => {
    try {
      await deleteRow("outbound_messages", id);
      setRows((list) => list.filter((row) => row.id !== id));
      setSelectedId(null);
      notify("Message deleted.");
    } catch {
      setError("We couldn't delete that message. Please try again.");
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (!query) return true;
      return [row.recipient, row.subject || "", row.body || ""].join(" ").toLowerCase().includes(query);
    });
  }, [rows, search, statusFilter]);

  const selected = rows.find((row) => row.id === selectedId) || null;

  const columns: Column<OutboundMessage>[] = [
    {
      key: "recipient",
      header: "Recipient",
      primary: true,
      render: (row) => (
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.recipient}</p>
          <p className="text-xs text-foreground-500 mt-0.5 line-clamp-1">{row.subject || row.body || "—"}</p>
        </div>
      ),
    },
    { key: "subject", header: "Subject", render: (row) => <span className="text-sm text-foreground-700 line-clamp-1 max-w-xs">{row.subject || "—"}</span> },
    { key: "status", header: "Status", render: (row) => <StatusPill {...metaFor(MESSAGE_STATUS, row.status)} /> },
    { key: "sent", header: "Sent", render: (row) => <span className="text-sm text-foreground-600 whitespace-nowrap">{formatDate(row.sent_at)}</span> },
  ];

  return (
    <div>
      <PageHeader
        eyebrow={config.eyebrow}
        title={config.title}
        subtitle={config.subtitle}
        actions={
          <button type="button" onClick={() => setComposeOpen(true)} className={btnPrimary}>
            <i className="ri-add-line"></i> {config.composeTitle}
          </button>
        }
      />

      <StatCards
        stats={[
          { label: "Total messages", value: rows.length, icon: config.icon, tone: "primary" },
          { label: "Sent", value: rows.filter((row) => row.status === "sent").length, icon: "ri-send-plane-line", tone: "primary" },
          { label: "Delivered / received", value: rows.filter((row) => row.status === "delivered" || row.status === "received").length, icon: "ri-check-double-line", tone: "secondary" },
          { label: "Failed", value: rows.filter((row) => row.status === "failed").length, icon: "ri-error-warning-line", tone: "accent" },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search by recipient, subject, content…" />
          <Select label="Status" value={statusFilter} onChange={setStatusFilter} options={[{ value: "all", label: "All statuses" }, { value: "sent", label: "Sent" }, { value: "delivered", label: "Delivered" }, { value: "received", label: "Received" }, { value: "failed", label: "Failed" }]} />
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">{filtered.length} messages</span>
        </FilterBar>

        {loading ? (
          <LoadingState label="Loading messages…" />
        ) : filtered.length === 0 ? (
          <EmptyState icon={config.icon} title={rows.length === 0 ? "No messages yet" : "No messages match"} message={`Messages sent over ${config.title.toLowerCase()} will appear here.`} />
        ) : (
          <DataTable columns={columns} rows={filtered} onRowClick={(row) => setSelectedId(row.id)} />
        )}
      </div>

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title={selected?.recipient || "Message"}
        subtitle={config.title}
        footer={
          selected ? (
            <button type="button" onClick={() => remove(selected.id)} className={btnGhost}>
              <i className="ri-delete-bin-line"></i> Delete
            </button>
          ) : null
        }
      >
        {selected ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill {...metaFor(MESSAGE_STATUS, selected.status)} />
            </div>
            <DetailList
              items={[
                { icon: "ri-user-line", label: "Recipient", value: selected.recipient },
                { icon: "ri-mail-line", label: config.subjectLabel, value: selected.subject || "—" },
                { icon: "ri-calendar-line", label: "Sent", value: formatDate(selected.sent_at) },
              ]}
            />
            <Field label="Message">
              <p className="text-sm text-foreground-800 leading-relaxed bg-background-100 border border-background-200 rounded-md p-4 whitespace-pre-wrap">
                {selected.body || "—"}
              </p>
            </Field>
          </>
        ) : null}
      </Drawer>

      <Modal
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        title={config.composeTitle}
        subtitle={config.title}
        footer={
          <>
            <button type="button" onClick={() => setComposeOpen(false)} className={btnGhost}>Cancel</button>
            <button type="button" onClick={send} disabled={saving} className={btnPrimary}>
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-send-plane-line"></i>}
              Send
            </button>
          </>
        }
      >
        <TextField label={config.recipientLabel}>
          <input value={draft.recipient} onChange={(e) => setDraft({ ...draft, recipient: e.target.value })} placeholder={config.recipientPlaceholder} className={textInput} />
        </TextField>
        <TextField label={config.subjectLabel}>
          <input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} placeholder={config.subjectPlaceholder} className={textInput} />
        </TextField>
        <TextField label="Message">
          <textarea value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} rows={5} maxLength={900} className={`${textInput} resize-none`}></textarea>
        </TextField>
      </Modal>

      <p className="text-xs text-foreground-500 mt-5">
        <i className="ri-information-line"></i> A full log of every message sent from this channel.
      </p>
      {node}
    </div>
  );
}